/**
 * sachi-article-enhancements.js
 * ─────────────────────────────
 * 1. Moves the FAQ section inside .f-article-template (at the end)
 * 2. Parses [product-handle] shortcodes in article content and renders
 *    inline product cards with image(s) on top, info + Add to Bag below.
 *
 * Shortcode syntax:
 *   [tpc]            → product with handle "tpc"
 *   [tpc img=3]      → same product, featured image + 3rd image side by side
 *   Two consecutive  → 2-column grid, img= dismissed, one image each
 *
 * Card layout:
 *   SINGLE  → images on top (50/50 if dual, full if single) | info below
 *   MULTI   → 2-col grid always (incl. mobile) — image top | info bottom
 */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    moveFaqIntoArticle();
    initShortcodes();
  });

  /* ──────────────────────────────────────────────────────────── */
  /*  Move FAQ into the article body container                    */
  /* ──────────────────────────────────────────────────────────── */

  function moveFaqIntoArticle() {
    var articleBody = document.querySelector('.f-article-template__content');
    var faq = document.querySelector('.sachi-article-faq');
    if (!articleBody || !faq) return;
    articleBody.appendChild(faq);

    // Moving a node triggers disconnectedCallback → destroy() then connectedCallback → init()
    // on every <collapsible-tab> inside it. destroy() tries removeEventListener with a new
    // anonymous function (never matches), so the original listener survives. init() then
    // adds a second listener — each click fires toggle() twice (open + close = net nothing).
    // Fix: replace each tab with a clean clone so it reconnects with exactly one fresh listener.
    requestAnimationFrame(function () {
      faq.querySelectorAll('collapsible-tab').forEach(function (tab) {
        var fresh = tab.cloneNode(true);
        tab.replaceWith(fresh);
      });
    });
  }

  /* ──────────────────────────────────────────────────────────── */
  /*  Shortcode engine                                            */
  /* ──────────────────────────────────────────────────────────── */

  function initShortcodes() {
    var dataEl = document.getElementById('sachi-featured-products-data');
    if (!dataEl) return;

    var products;
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var content = document.querySelector('.f-article-template__content');
    if (!content || !products.length) return;

    // Build handle → product lookup map (case-insensitive)
    var handleMap = {};
    products.forEach(function (p) {
      handleMap[p.handle.toLowerCase()] = p;
    });

    // Step 1: Replace [handle] / [handle img=N] with span placeholders
    // Pattern: [word-word] or [word-word img=3]
    // Only replaces if the handle exists in handleMap
    var shortcodePattern = /\[([a-zA-Z0-9][a-zA-Z0-9_-]*)(?:\s+img=(\d+))?\]/gi;
    var html = content.innerHTML;
    var matchCount = 0;

    html = html.replace(shortcodePattern, function (match, handle, imgNum) {
      var product = handleMap[handle.toLowerCase()];
      if (!product) return match; // Not a known product handle — leave untouched
      matchCount++;
      var imgIndex = imgNum ? parseInt(imgNum, 10) - 1 : -1;
      return (
        '<span class="sachi-tpc-ph" data-handle="' +
        esc(product.handle) +
        '" data-img="' +
        imgIndex +
        '"></span>'
      );
    });

    if (matchCount === 0) return;

    content.innerHTML = html;

    // Step 2: Promote every placeholder to a block-level product card.
    //
    // The RTE often embeds multiple shortcodes and following text inside the
    // same <p>, e.g.: <p>[ph1][ph2]<br><br><strong>Next heading</strong></p>
    //
    // Strategy per placeholder:
    //   a) Walk up to the direct child of `content` (the wrapper, usually a <p>)
    //   b) Collect all sibling nodes that follow ph within that <p> — these are
    //      content that must appear AFTER the card, not before it
    //   c) Insert the card after wrapper
    //   d) Move the trailing nodes into a new <p> inserted after the card
    //   e) Clean up the wrapper if now empty
    //
    // When there are two consecutive shortcodes in the same <p>, processing ph1
    // moves ph2 + trailing content into a new <p>. ph2's forEach iteration then
    // finds that new <p> as its wrapper and applies the same split — resulting
    // in correct order: card1, card2, <p>trailing content</p>.

    var placeholders = Array.from(content.querySelectorAll('.sachi-tpc-ph'));
    var productCards = [];

    placeholders.forEach(function (ph) {
      var card = document.createElement('div');
      card.className = 'sachi-inline-product';
      card.dataset.handle = ph.dataset.handle;
      card.dataset.imgIndex = ph.dataset.img;

      // Walk up to the direct child of `content`
      var wrapper = ph;
      while (wrapper.parentNode && wrapper.parentNode !== content) {
        wrapper = wrapper.parentNode;
      }

      if (wrapper === ph) {
        // ph is already a direct child of content — simple in-place replacement
        content.insertBefore(card, ph);
        ph.remove();
      } else {
        // ph is inside a wrapper (typically a <p>).
        // Collect every sibling node that comes AFTER ph — these need to end up
        // after the card so they render below it, not above.
        var trailingNodes = [];
        var node = ph.nextSibling;
        while (node) {
          var next = node.nextSibling;
          trailingNodes.push(node);
          node = next;
        }

        // Remove placeholder from its parent
        ph.remove();

        // Insert card immediately after wrapper
        var refNode = wrapper.nextSibling;
        if (refNode) {
          content.insertBefore(card, refNode);
        } else {
          content.appendChild(card);
        }

        // Move trailing nodes to a new <p> after the card, stripping any
        // leading <br> spacing artifacts first
        if (trailingNodes.length > 0) {
          while (trailingNodes.length > 0 && trailingNodes[0].nodeName === 'BR') {
            trailingNodes.shift();
          }
          if (trailingNodes.length > 0) {
            var trail = document.createElement('p');
            trailingNodes.forEach(function (n) { trail.appendChild(n); });
            var afterCard = card.nextSibling;
            if (afterCard) {
              content.insertBefore(trail, afterCard);
            } else {
              content.appendChild(trail);
            }
          }
        }

        // Clean up wrapper if now empty
        if (!wrapper.textContent.replace(/\u00a0/g, '').trim() &&
            !wrapper.querySelector('img, video, iframe')) {
          wrapper.remove();
        }
      }

      productCards.push(card);
    });

    // Step 3: Group consecutive block-level product cards
    var groups = groupConsecutive(productCards);

    // Step 4: Render card HTML into each element
    groups.forEach(function (group) {
      if (group.length === 1) {
        var card = group[0];
        var product = handleMap[card.dataset.handle];
        if (!product) return;
        var imgIdx = parseInt(card.dataset.imgIndex, 10);
        card.classList.add('sachi-inline-product--single');
        var dual = imgIdx >= 0;
        if (dual) card.classList.add('sachi-inline-product--dual');
        card.innerHTML = buildCard(product, imgIdx, dual);
      } else {
        // Multiple consecutive block cards → wrap in 2-col grid
        var grid = document.createElement('div');
        grid.className = 'sachi-inline-product-grid';
        group[0].parentNode.insertBefore(grid, group[0]);
        group.forEach(function (card) {
          var prod = handleMap[card.dataset.handle];
          if (prod) {
            card.innerHTML = buildCard(prod, -1, false);
          }
          grid.appendChild(card);
        });
      }
    });

    // Step 5: Bind lightbox on dual second images (img=N cards).
    // Uses the theme's already-loaded PhotoSwipeLightbox so the popup
    // matches the site's native image zoom experience exactly.
    content.querySelectorAll('.sachi-tpc-dual-img img').forEach(function (img) {
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openPhotoSwipe(img.src);
      });
    });
  }

  /* ──────────────────────────────────────────────────────────── */
  /*  Card builder                                                */
  /*  Outputs HTML matching the theme's product-card snippet      */
  /*  so all existing product-card__* CSS applies automatically.  */
  /* ──────────────────────────────────────────────────────────── */

  /**
   * @param {Object}  product    Product data from JSON
   * @param {number}  imgIdx     0-based image index (-1 = featured only)
   * @param {boolean} dual       Show featured + extra image side by side
   */
  function buildCard(product, imgIdx, dual) {
    // ── Image(s) ─────────────────────────────────────────────────
    var mainImgHtml =
      '<responsive-image class="f-image product-card__image product-card__image--main" style="--aspect-ratio: 0.8">' +
        '<img src="' + esc(product.featured_image || '') + '"' +
        ' alt="' + esc(product.title) + '"' +
        ' loading="lazy" width="800" height="1000" class="motion-reduce">' +
      '</responsive-image>';

    var secondImgHtml = '';
    if (dual && product.images && product.images[imgIdx]) {
      secondImgHtml =
        '<responsive-image class="f-image product-card__image sachi-tpc-dual-img" style="--aspect-ratio: 0.8">' +
          '<img src="' + esc(product.images[imgIdx]) + '"' +
          ' alt="' + esc(product.title) + '"' +
          ' loading="lazy" width="800" height="1000" class="motion-reduce">' +
        '</responsive-image>';
    }

    var imageWrapperHtml;
    if (dual && secondImgHtml) {
      // Dual mode: no full-width overlay (it would block the second image click).
      // Wrap only the main image in its own <a> so it still links to the product.
      imageWrapperHtml =
        '<div class="product-card__image-wrapper f-card sachi-tpc-dual-wrapper">' +
          '<a href="' + esc(product.url) + '" class="sachi-tpc-main-link" aria-label="' + esc(product.title) + '">' +
            mainImgHtml +
          '</a>' +
          secondImgHtml +
        '</div>';
    } else {
      // Single image: use the standard theme full-width overlay link.
      imageWrapperHtml =
        '<div class="product-card__image-wrapper f-card">' +
          '<a href="' + esc(product.url) + '" class="full-width-link product-card__link" tabindex="-1" aria-hidden="true"></a>' +
          mainImgHtml +
        '</div>';
    }

    // ── Title ("Name - Variant" pattern used by this theme) ──────
    var parts = product.title.split(' - ');
    var mainTitle = esc(parts[0]);
    var variantTitleHtml = parts[1]
      ? '<span class="product-card__title-variant">[' + esc(parts[1]) + ']</span>'
      : '';

    // ── Tagline ──────────────────────────────────────────────────
    var taglineHtml = product.tagline
      ? '<p>' + esc(product.tagline) + '</p>'
      : '';

    // ── Rating (matches theme's f-product-rating structure) ──────
    var ratingHtml = '';
    if (product.rating != null && product.rating_max != null) {
      var rounded = (Math.round(product.rating * 10) / 10).toFixed(1);
      var ratingFloor = Math.floor(product.rating);
      var decimal = product.rating % 1;
      var ratingDecimal = 0;
      if (decimal >= 0.3 && decimal <= 0.7) ratingDecimal = 0.5;
      else if (decimal > 0.7) ratingDecimal = 1;

      ratingHtml =
        '<div class="product-card__reviews">' +
          '<div class="f-product-rating" style="--star-size: 1; --star-color: #222222">' +
            '<div class="rating" role="img"' +
            ' aria-label="' + rounded + ' out of ' + product.rating_max + '">' +
              '<span aria-hidden="true" class="rating-star"' +
              ' style="--rating: ' + ratingFloor + '; --rating-max: ' + product.rating_max + '; --rating-decimal: ' + ratingDecimal + ';"></span>' +
            '</div>' +
            '<p class="rating-text text-subtext"><span aria-hidden="true">' + rounded + ' / ' + product.rating_max + '</span></p>' +
            (product.rating_count != null
              ? '<p class="rating-count text-subtext small-bold" style="opacity:0.5"><span aria-hidden="true">(' + product.rating_count + ')</span></p>'
              : '') +
          '</div>' +
        '</div>';
    }

    // ── Price (matches theme's f-price structure) ─────────────────
    var onSale = !!product.compare_at_price;
    var priceHtml =
      '<div class="f-price' + (onSale ? ' f-price--on-sale' : '') + '">' +
        '<div class="f-price__regular">' +
          '<span class="f-price-item f-price-item--regular">' + esc(product.price) + '</span>' +
        '</div>' +
        '<div class="f-price__sale">' +
          '<span class="f-price-item f-price-item--sale">' + esc(product.price) + '</span>' +
          (onSale
            ? '<span><s class="f-price-item f-price-item--regular">' + esc(product.compare_at_price) + '</s></span>'
            : '') +
        '</div>' +
      '</div>';

    // ── Add to Bag: use product-form web component so drawer/events work ─
    var atcHtml;
    if (!product.available) {
      atcHtml =
        '<div class="product-card__actions">' +
          '<button class="product-card__atc btn btn--white" disabled aria-label="Sold out">' +
            '<span class="product-card__atc-text product-card__action-text">Sold Out</span>' +
          '</button>' +
        '</div>';
    } else if (product.variant_id) {
      var formId = 'sachi-tpc-form-' + esc(product.handle);
      atcHtml =
        '<div class="product-card__actions">' +
          '<product-form class="f-product-form w-full product-card__action-button" data-type="product-card">' +
            '<form id="' + formId + '" class="product-card-form" novalidate>' +
              '<input type="hidden" name="id" value="' + esc(String(product.variant_id)) + '">' +
              '<button class="product-card__atc btn" name="add"' +
              ' aria-label="Add to cart">' +
                '<span class="product-card__atc-icon product-card__action-icon hidden"></span>' +
                '<span class="product-card__action-icon-loading f-spinner-icon">' +
                  '<svg aria-hidden="true" class="f-icon-svg f-icon-svg--medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle fill="none" cx="33" cy="33" r="30"/></svg>' +
                '</span>' +
                '<span class="product-card__atc-text product-card__action-text">Add to Bag</span>' +
              '</button>' +
            '</form>' +
          '</product-form>' +
        '</div>';
    } else {
      atcHtml =
        '<div class="product-card__actions">' +
          '<a href="' + esc(product.url) + '" class="product-card__atc btn btn--white product-card__action-button">' +
            '<span class="product-card__atc-text product-card__action-text">Shop Now</span>' +
          '</a>' +
        '</div>';
    }

    // ── Info block ───────────────────────────────────────────────
    var infoHtml =
      '<div class="product-card__info text-center">' +
        '<div class="product-card__info_title-wrapper">' +
          '<h3 class="product-card__title text-product-title">' +
            '<a href="' + esc(product.url) + '">' + mainTitle + '</a>' +
            variantTitleHtml +
          '</h3>' +
          taglineHtml +
        '</div>' +
        '<div class="product-card__info-inner">' +
          '<div class="product-card__info-inner-price-rating">' +
            priceHtml +
            ratingHtml +
          '</div>' +
          atcHtml +
        '</div>' +
      '</div>';

    return imageWrapperHtml + infoHtml;
  }

  /* ──────────────────────────────────────────────────────────── */
  /*  Minimal custom lightbox                                     */
  /* ──────────────────────────────────────────────────────────── */

  var _lightboxEl = null;

  function openPhotoSwipe(src) {
    // Reuse existing overlay element if already in DOM
    if (!_lightboxEl) {
      _lightboxEl = document.createElement('div');
      _lightboxEl.className = 'sachi-lightbox';
      _lightboxEl.setAttribute('role', 'dialog');
      _lightboxEl.setAttribute('aria-modal', 'true');
      _lightboxEl.setAttribute('aria-label', 'Image');

      var closeBtn =
        '<button class="sachi-lightbox__close" aria-label="Close">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true">' +
            '<line x1="4" y1="4" x2="20" y2="20"/>' +
            '<line x1="20" y1="4" x2="4" y2="20"/>' +
          '</svg>' +
        '</button>';
      var imgTag = '<img class="sachi-lightbox__img" alt="">';
      _lightboxEl.innerHTML = closeBtn + imgTag;

      // Close on backdrop click
      _lightboxEl.addEventListener('click', function (e) {
        if (e.target === _lightboxEl) closeLightbox();
      });
      // Close button
      _lightboxEl.querySelector('.sachi-lightbox__close').addEventListener('click', closeLightbox);
      // Escape key
      document.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.keyCode === 27) && _lightboxEl && _lightboxEl.classList.contains('is-open')) {
          closeLightbox();
        }
      });

      document.body.appendChild(_lightboxEl);
    }

    _lightboxEl.querySelector('.sachi-lightbox__img').src = src;
    document.body.style.overflow = 'hidden';
    // Force reflow so transition plays
    _lightboxEl.style.display = 'flex';
    // eslint-disable-next-line no-unused-expressions
    _lightboxEl.offsetHeight;
    _lightboxEl.classList.add('is-open');
    _lightboxEl.querySelector('.sachi-lightbox__close').focus();
  }

  function closeLightbox() {
    if (!_lightboxEl) return;
    _lightboxEl.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () {
      if (_lightboxEl) _lightboxEl.style.display = 'none';
    }, 200);
  }

  /* ──────────────────────────────────────────────────────────── */
  /*  Grouping helpers                                            */
  /* ──────────────────────────────────────────────────────────── */

  function groupConsecutive(cards) {
    if (!cards.length) return [];
    var groups = [[cards[0]]];

    for (var i = 1; i < cards.length; i++) {
      var prev = cards[i - 1];
      var curr = cards[i];

      if (areAdjacent(prev, curr)) {
        groups[groups.length - 1].push(curr);
      } else {
        groups.push([curr]);
      }
    }
    return groups;
  }

  function areAdjacent(el1, el2) {
    var node = el1.nextSibling;
    var steps = 0;
    while (node && steps < 10) {
      if (node === el2) return true;
      if (node.nodeType === 3 && !node.textContent.trim()) {
        node = node.nextSibling; steps++; continue;
      }
      if (node.nodeType === 1) {
        if (node.tagName === 'BR') { node = node.nextSibling; steps++; continue; }
        if (node.tagName === 'P' && !node.textContent.trim() && !node.children.length) {
          node = node.nextSibling; steps++; continue;
        }
      }
      return false;
    }
    return false;
  }

  /** HTML-safe escaping */
  function esc(str) {
    if (!str) return '';
    var el = document.createElement('span');
    el.textContent = String(str);
    return el.innerHTML;
  }
})();

